// Specialties offered in the referral dialog and the template editor. The
// English name is what the letter prompt receives (the letter is written in
// the note language); the list shows `referral.specialties.<key>`. Any other
// specialty or service can be typed in.
export const REFERRAL_SPECIALTIES = [
  ["allergy", "Allergy and Clinical Immunology"],
  ["cardiology", "Cardiology"],
  ["cardiac-surgery", "Cardiac Surgery"],
  ["dermatology", "Dermatology"],
  ["endocrinology", "Endocrinology"],
  ["gastroenterology", "Gastroenterology"],
  ["general-surgery", "General Surgery"],
  ["geriatrics", "Geriatric Medicine"],
  ["gynecology", "Gynecology"],
  ["hematology", "Hematology"],
  ["infectious-diseases", "Infectious Diseases"],
  ["internal-medicine", "Internal Medicine"],
  ["nephrology", "Nephrology"],
  ["neurology", "Neurology"],
  ["neurosurgery", "Neurosurgery"],
  ["obstetrics", "Obstetrics"],
  ["oncology", "Oncology"],
  ["ophthalmology", "Ophthalmology"],
  ["orthopedics", "Orthopedic Surgery"],
  ["otolaryngology", "Otolaryngology (ENT)"],
  ["pain", "Pain Medicine"],
  ["palliative", "Palliative Care"],
  ["pediatrics", "Pediatrics"],
  ["physiatry", "Physical Medicine and Rehabilitation"],
  ["plastic-surgery", "Plastic Surgery"],
  ["psychiatry", "Psychiatry"],
  ["respirology", "Respirology"],
  ["rheumatology", "Rheumatology"],
  ["sleep", "Sleep Medicine"],
  ["sports-medicine", "Sports Medicine"],
  ["urology", "Urology"],
  ["vascular-surgery", "Vascular Surgery"],
  ["physiotherapy", "Physiotherapy"],
  ["dietitian", "Dietitian"],
  ["mental-health", "Mental Health Counselling"],
] as const;
export type ReferralSpecialtyKey = (typeof REFERRAL_SPECIALTIES)[number][0];
export type SpecialtyOption = {
  key: ReferralSpecialtyKey;
  name: string;
  label: string;
};

const fold = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export const specialtyOptions = (
  label: (key: ReferralSpecialtyKey) => string,
): SpecialtyOption[] =>
  REFERRAL_SPECIALTIES.map(([key, name]) => ({
    key,
    name,
    label: label(key),
  })).sort((a, b) => a.label.localeCompare(b.label));

/**
 * Options whose label or English name contains every typed word: names that
 * start with the text first, then names with a word that starts with it.
 */
export const filterSpecialties = (
  options: readonly SpecialtyOption[],
  query: string,
): SpecialtyOption[] => {
  const typed = fold(query);
  const words = typed.split(" ").filter(Boolean);
  if (!words.length) return [...options];
  const rank = (option: SpecialtyOption): number => {
    const names = [fold(option.label), fold(option.name)];
    if (names.some((name) => name.startsWith(typed))) return 0;
    const starts = names.some((name) =>
      words.every((word) =>
        name.split(/[\s()/-]+/).some((part) => part.startsWith(word)),
      ),
    );
    return starts ? 1 : 2;
  };
  return options
    .filter((option) => {
      const haystack = `${fold(option.label)} ${fold(option.name)}`;
      return words.every((word) => haystack.includes(word));
    })
    .map((option, index) => ({ option, index, rank: rank(option) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ option }) => option);
};

/** The English name for a label or name typed or picked; else the text as typed. */
export const specialtyValue = (
  options: readonly SpecialtyOption[],
  text: string,
): string => {
  const typed = text.replace(/\s+/g, " ").trim();
  const match = options.find(
    (option) =>
      fold(option.label) === fold(typed) || fold(option.name) === fold(typed),
  );
  return match ? match.name : typed;
};
